import { cn } from "@/lib/utils"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { Quote } from "lucide-react"

export interface TestimonialAuthor {
  name: string
  handle: string
  avatar?: string
  initials?: string
}

export interface TestimonialCardProps {
  author: TestimonialAuthor
  text: string
  href?: string
  className?: string
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function TestimonialCard({ 
  author,
  text,
  href,
  className
}: TestimonialCardProps) {
  const Card = href ? 'a' : 'div'
  
  return (
    <Card 
      {...(href ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
      className={cn(
        // About page card styling - mobile optimized
        "group relative p-4 md:p-6 h-full bg-card border border-border rounded-xl md:rounded-2xl hover:bg-muted/30 transition-colors",
        href && "cursor-pointer",
        className
      )}
    >
      {/* Quote icon - matching About page icon styling */}
      <div className="p-2 md:p-3 rounded-lg md:rounded-xl bg-muted/50 group-hover:bg-background transition-colors w-fit mb-3 md:mb-5">
        <Quote className="h-4 w-4 md:h-5 md:w-5 text-foreground" strokeWidth={1.5} />
      </div>

      {/* Testimonial text */}
      <p className="text-muted-foreground text-xs md:text-sm leading-relaxed mb-4 md:mb-6">
        "{text}"
      </p>

      {/* Author info */}
      <div className="flex items-center gap-2.5 md:gap-3 mt-auto pt-3 md:pt-4 border-t border-border/50">
        <Avatar className="h-8 w-8 md:h-10 md:w-10 ring-2 ring-primary/40 ring-offset-1 md:ring-offset-2 ring-offset-background">
          {author.avatar && (
            <AvatarImage src={typeof author.avatar === "string" ? author.avatar : (author.avatar as { default: string }).default} alt="" />
          )}
          <AvatarFallback className="bg-primary text-primary-foreground text-[10px] md:text-xs font-semibold">
            {author.initials || getInitials(author.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <span className="text-xs md:text-sm font-semibold text-foreground leading-tight">
            {author.name}
          </span>
          <span className="text-[10px] md:text-xs text-muted-foreground">
            {author.handle}
          </span>
        </div>
      </div>
    </Card>
  )
}
